import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { CachePersistor } from 'apollo3-cache-persist';
import localForage from 'localforage';
import apolloPossibleTypes from '../../graphql/generated/apolloPossibleTypes';
import Logger from '../../utils/Logger';
import authLink from './authLink';
import errorLink from './errorLink';
import parseJsonLink from './parseJsonLink';
import transportLink from './transportLink';
import typePolicies from './typePolicies';

const CACHE_MAX_SIZE_MB = 100;
const CACHE_SCHEMA_VERSION_KEY = 'apolloCacheVersion';
const CACHE_KEY = 'apolloCache';

const isDev = process.env.NODE_ENV !== 'production';
const apolloLinks = [authLink, errorLink, parseJsonLink, transportLink];

if (isDev) {
  // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
  const apolloLogger = require('apollo-link-logger').default;

  apolloLinks.unshift(apolloLogger);
}

const cache = new InMemoryCache({
  possibleTypes: apolloPossibleTypes.possibleTypes,
  typePolicies,
});

const localForageInstance = localForage.createInstance({
  name: 'slab-cache-persister-a3',
});

class LocalForageWrapper {
  constructor(private storage: LocalForage) {}

  getItem(key: string): string | Promise<string | null> | null {
    return this.storage.getItem(key);
  }

  removeItem(key: string): void | Promise<void> {
    return this.storage.removeItem(key);
  }

  setItem(key: string, value: string): void | Promise<void> {
    return new Promise((resolve) => {
      this.storage
        .setItem(key, value)
        .then(() => resolve())
        .catch((err) => {
          Logger.warn(err);
          cachePersistor.remove();
        });
    });
  }
}

const cachePersistor = new CachePersistor({
  cache,
  debug: isDev,
  key: CACHE_KEY,
  maxSize: CACHE_MAX_SIZE_MB * 1024 * 1024,
  storage: new LocalForageWrapper(localForageInstance),
});

const client = new ApolloClient({
  cache,
  link: ApolloLink.from(apolloLinks),
  assumeImmutableResults: true,
  connectToDevTools: isDev,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      // After Apollo 3.1, 'cache-and-network` means whenever the cached value changes, a new network request is triggered.
      // in order to avoid this unnecessary effect (which could even lead to infinite loops), nextFetchPolicy is needed.
      // recommended setting to from https://github.com/apollographql/apollo-client/pull/6712#issuecomment-679446404
      nextFetchPolicy(lastFetchPolicy) {
        if (
          lastFetchPolicy === 'cache-and-network' ||
          lastFetchPolicy === 'network-only'
        ) {
          return 'cache-first';
        }
        return lastFetchPolicy;
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
let cacheRestored = false;

export function purgeApolloCache() {
  cachePersistor.purge();
}

export default async function getApolloClient() {
  if (cacheRestored) return client;
  const currentVersion = process.env.GRAPHQL_SCHEMA_HASH as string;

  if (localStorage.getItem('authToken')) {
    try {
      if (currentVersion === localStorage.getItem(CACHE_SCHEMA_VERSION_KEY)) {
        await cachePersistor.restore();
      } else {
        await cachePersistor.purge();
        localStorage.setItem(CACHE_SCHEMA_VERSION_KEY, currentVersion);
      }
    } catch (error) {
      Logger.error(error);
    }
  }

  cacheRestored = true;
  return client;
}
